import { Divider, Empty, Select, SelectProps, Spin, Tag } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { ReactElement, useRef, useState } from 'react';
import { debounce } from 'lodash-es';
import { searchArticle } from 'api/document';
import { RefSelectProps } from 'antd/lib/select';
import { FileTag, NpiArticle, VendorFile } from 'models/Article';
import { useNavigate } from 'react-router-dom';
import './DocumentSearch.scss';

/**
 * Represents a document search component.
 * @typedef {React.FC<SelectProps>} DocumentSearch
 */
export const DocumentSearch: React.FC<SelectProps> = ({ ...rest }) => {
  const navigate = useNavigate();
  const ref = useRef<RefSelectProps>(null);
  const [loading, setLoading] = useState(false);
  const [articles, setArticles] = useState<NpiArticle[]>([]);
  const [searchStr, setSearchStr] = useState<undefined | string>(undefined);

  const searchWidth = 200;
  const dropdownWidth = 420;

  /**
   * A debounced function that performs a search.
   *
   * @param {string} str - The search string.
   * @return {Promise<void>} A promise that resolves when the search is complete.
   */
  const onSearch = debounce(async (str: string): Promise<void> => {
    setSearchStr(str);
    if (str.length >= 2) {
      setLoading(true);
      try {
        const articles: NpiArticle[] = await searchArticle(str);
        setArticles(
          articles.map((a) => {
            a.label = a.name;
            return a;
          })
        );
      } finally {
        setLoading(false);
      }
    } else {
      setArticles([]);
    }
  }, 180);

  /**
   * Renders the vendors in JSX format.
   *
   * @param {VendorFile[]} items - An array of VendorFile objects containing vendor information.
   * @returns {React.ReactElement[]} An array of JSX.Element objects representing the rendered vendors.
   */
  const renderVendors = (items: VendorFile[]): ReactElement[] => {
    return items.map((item: VendorFile) => (
      <Tag className="article-tag" key={item.vendor.id}>
        {item.vendor.name}
      </Tag>
    ));
  };

  /**
   * Renders an array of file tags as React elements.
   *
   * @param {FileTag[]} items - The array of file tags to render.
   * @returns {React.ReactElement[]} - The array of React elements representing the file tags.
   */
  const renderTags = (items: FileTag[]): ReactElement[] => {
    return items.map((item: FileTag) => (
      <Tag className="article-tag" key={item.tag.id}>
        {item.tag.name}
      </Tag>
    ));
  };

  /**
   * Function to handle the selection of a resource.
   *
   * @param {number} resource_id - The ID of the selected resource.
   * @returns {void}
   */
  const handleSelect = (resource_id: number): void => {
    setSearchStr(undefined);
    setArticles([]);
    navigate(`/resources?resource_id=${resource_id}`);
  };

  return (
    <div className="document-search">
      <Select
        placeholder={'Search Resources'}
        size="large"
        allowClear
        showArrow
        showSearch
        className="document-search"
        value={searchStr}
        bordered
        virtual={false}
        suffixIcon={<SearchOutlined />}
        filterOption={false}
        style={{ width: searchWidth }}
        dropdownStyle={{ minWidth: dropdownWidth }}
        dropdownRender={(menu) => (
          <>
            <div className="text-xl pl-4 pt-2">NPI Resources</div>
            <Divider style={{ margin: '8px 0 0' }} />
            {menu}
          </>
        )}
        notFoundContent={loading ? <Spin size="default" /> : <Empty />}
        ref={ref}
        onSearch={onSearch}
        onSelect={handleSelect}
        {...rest}
      >
        {articles.map((article) => (
          <Select.Option key={article.id} value={article.id} className="doc">
            <>
              <div className="article-category">{article.lov_file_category.name}</div>
              <div className="article-name">{article.name}</div>
              <div>
                <span className="text-slate-400 mr-2 text-xs">Tags:</span>
                {renderVendors(article.vendor_file)}
                {renderTags(article.file_tag)}
              </div>
            </>
          </Select.Option>
        ))}
      </Select>
    </div>
  );
};

export default DocumentSearch;
